import React, { useCallback, useMemo } from 'react';
import { Translate as t } from '../../config/Translate';
import { isRenewalLicense, formatLicenseDate } from '../../lib/licenseHelper';
import { renderHeader } from './columns';
import useClientTable from './useClientTable';
import TablePager from './TablePager';
import SortableHeader from './SortableHeader';

const DEFAULT_SORT = { key: 'issuedAt', dir: 'desc' };

// 정렬은 표시 문자열이 아니라 원값(타임스탬프·숫자)으로 한다.
const makeGetSortValue = (companyNameOf) => (qr, key) => {
    switch (key) {
        case 'issuedAt': return typeof qr?.issuedAt === 'number' ? qr.issuedAt : null;
        case 'validUntil': return typeof qr?.validuntil === 'number' ? qr.validuntil : null;
        case 'type': return qr?.type ? (isRenewalLicense(qr) ? 1 : 0) : null;
        case 'status': return (qr?.used ?? 0) > 0 ? 1 : 0;
        case 'company': {
            // 표시 이름(companyNameOf)으로 정렬 — 없으면 id.
            if (!qr?.dealerId) return null;
            const name = typeof companyNameOf === 'function' ? companyNameOf(qr.dealerId) : null;
            return name ?? qr.dealerId;
        }
        default: return null;
    }
};

const IssueHistoryTable = ({
    items,
    canOperate = false,
    onReprint,
    showCompany = false,
    companyNameOf,
}) => {
    const rows = Array.isArray(items) ? items : [];
    const getSortValue = useMemo(() => makeGetSortValue(companyNameOf), [companyNameOf]);
    const { pageRows, sortKey, sortDir, toggleSort, page, setPage, pageSize, setPageSize, total } =
        useClientTable(rows, { getSortValue, defaultSort: DEFAULT_SORT });

    const sortNode = useCallback((key, label) => (
        <SortableHeader label={label} active={sortKey === key} dir={sortDir} onToggle={() => toggleSort(key)} />
    ), [toggleSort, sortKey, sortDir]);

    // 작업 열은 보여줄 동작(재출력)이 있을 때만 그린다.
    const columns = [
        ...(showCompany ? [{ key: 'company', node: sortNode('company', 'common.label.company') }] : []),
        { key: 'issuedAt', node: sortNode('issuedAt', 'license.label.issuedAt') },
        { key: 'validUntil', node: sortNode('validUntil', 'license.field.validUntil') },
        { key: 'type', node: sortNode('type', 'common.field.type'), style: { whiteSpace: 'nowrap' } },
        { key: 'status', node: sortNode('status', 'common.label.status'), style: { whiteSpace: 'nowrap' } },
        ...(canOperate ? [{ key: 'actions', label: 'common.label.actions' }] : []),
    ];

    return (
        <div className="mb-2">
            <div className="table-responsive">
                <table className="table table-sm table-bordered">
                    <thead className="sticky-top bg-white" style={{ zIndex: 1 }}>
                        <tr>{renderHeader(columns)}</tr>
                    </thead>
                    <tbody className="align-middle">
                        {pageRows.length === 0 ? (
                            <tr><td colSpan={columns.length} className="text-center text-muted">{t('common.message.noData')}</td></tr>
                        ) : (
                            pageRows.map((qr, i) => {
                                // PR #241 review (Arch minor) — SSoT 통일: isRenewalLicense.
                                const isRenewal = isRenewalLicense(qr);
                                const typeLabel = qr?.type ? (isRenewal ? t('license.label.renewal') : t('license.label.initial')) : '-';
                                const activated = (qr?.used ?? 0) > 0;
                                const statusKey = activated ? 'Activated' : 'Pending Activation';
                                const companyName = qr?.dealerId
                                    ? (typeof companyNameOf === 'function' ? companyNameOf(qr.dealerId) : null) ?? qr.dealerId
                                    : '-';
                                return (
                                    <tr key={qr?.sk || qr?._id || i}>
                                        {showCompany && <td>{companyName}</td>}
                                        {/* IMP-160 — 시간까지 표시 (formatLicenseDate 내부 toLocaleString). */}
                                        <td>{formatLicenseDate(qr?.issuedAt)}</td>
                                        <td>{formatLicenseDate(qr?.validuntil)}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{typeLabel}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{t(statusKey)}</td>
                                        {/* RTSQA-339 round 14: list 액션 버튼 정렬 보정 — margin-left 1px.
                                            BUG-185: self-dealing 아닌 부모 대행 발행 차단 — canOperate=false 면 열 자체를 그리지 않는다. */}
                                        {canOperate && (
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary py-0"
                                                    style={{ fontSize: '0.75rem', margin: '0 0 0 1px' }}
                                                    disabled={!qr?.batchId || activated}
                                                    onClick={() => onReprint && onReprint(qr)}
                                                >
                                                    {t('license.button.reprint')}
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            <TablePager
                total={total}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
            />
        </div>
    );
};

export default IssueHistoryTable;
